import React, { createContext, useContext } from 'react';
import type { FC, ReactNode } from 'react';
import { Spinner } from '@wix/cli-ui-kit';
import type { DevMetadata } from '@wix/cli-astro-definitions';
import { useDevCenterUpdater } from './hooks/useDevCenterUpdater.js';
import { TunnelingConsent } from './TunnelingConsent.js';
import type { DevCenterWatcher } from './utils/DevCenterWatcher.js';

interface DevEnvironment {
  baseUrl: string;
  versionOverrideId: string;
  devCenterWatcher: DevCenterWatcher;
}

const DevEnvironmentContext = createContext<DevEnvironment | null>(null);

interface DevEnvironmentProviderProps {
  siteId: string;
  serverLocalUrl: string;
  serverExposedUrl?: string;
  devMetadata: DevMetadata | null;
  children: ReactNode;
}

export const DevEnvironmentProvider: FC<DevEnvironmentProviderProps> = ({
  siteId,
  serverLocalUrl,
  serverExposedUrl,
  devMetadata,
  children,
}) => {
  const { baseUrl, tunnelRequired, versionOverrideId, devCenterWatcher } =
    useDevCenterUpdater({
      siteId,
      serverLocalUrl,
      serverExposedUrl,
      devMetadata,
    });

  return (
    <TunnelingConsent required={tunnelRequired}>
      {versionOverrideId == null ? (
        <Spinner text="Preparing your dashboard..." />
      ) : (
        <DevEnvironmentContext.Provider
          value={{ baseUrl, versionOverrideId, devCenterWatcher }}
        >
          {children}
        </DevEnvironmentContext.Provider>
      )}
    </TunnelingConsent>
  );
};

export const useDevEnvironment = (): DevEnvironment => {
  const ctx = useContext(DevEnvironmentContext);
  if (!ctx) {
    throw new Error(
      'useDevEnvironment must be used inside DevEnvironmentProvider'
    );
  }
  return ctx;
};
